import {useContext, useEffect, useState} from 'react'
import {Flex, Form, Modal, Typography} from 'antd'
import {useIntl} from 'react-intl'
import {getRequest, postRequest} from '../../../../services/base'
import {NavContext} from '../../../../context/global_context'
import {notifyError} from '../../../../utils/notifications'
import useDebouncedValue from '../../../../utils/common_function'
import SelectInput from '../../../common/SelectInput'
import Button from '../../../common/Button'

const PlantVehiclesForm = ({ showModal, setShowModal, selectedPlant }) => {
  const intl = useIntl()
  const [, navDispatch] = useContext(NavContext)
  const [form] = Form.useForm()

  const [vehiclesOptions, setVehiclesOptions] = useState([])
  const [selectedVehicles, setSelectedVehicles] = useState([])
  const [search, setSearch] = useState('')
  const [vehicleSearchFailed, setVehicleSearchFailed] = useState(false)
  const [loading, setLoading] = useState(false)
  const debouncedSearch = useDebouncedValue(search, 500)

  const getSelectedVehicles = async (plantId) => {
    let res = await getRequest(`/api/plants/vehicles/${plantId}/`)
    // Loaded once when the modal opens. Falling through would silently present
    // an empty selection, which reads as "this plant has no make models".
    if (res?.status === false) {
      notifyError(navDispatch, intl.formatMessage({ id: 'plants.vehiclesLoadError' }))
      return
    }
    const data = res?.data || []
    setSelectedVehicles(data)
    form.setFieldsValue({
      vehicles: data.map((vehicle) => vehicle.id),
    })
    // Keep already-assigned vehicles selectable/visible even if the current
    // async options list does not include them (e.g. beyond the first page).
    setVehiclesOptions((prev) => {
      const map = new Map(prev.map((option) => [option.value, option]))
      data.forEach((vehicle) => {
        if (!map.has(vehicle.id)) {
          map.set(vehicle.id, {
            value: vehicle.id,
            label: vehicle.name,
            model: vehicle.model,
          })
        }
      })
      return Array.from(map.values())
    })
  }

  const fetchVehicles = async (searchTerm = '') => {
    setLoading(true)
    try {
      let res = await getRequest(`/api/vehicles/?search=${searchTerm}`)
      // Search-as-you-type: shown in the dropdown, not as a toast per keystroke.
      if (res?.status === false) {
        setVehicleSearchFailed(true)
        return
      }
      setVehicleSearchFailed(false)
      const results = res?.results || []
      setVehiclesOptions((prev) => {
        const map = new Map(
          results.map((item) => ({
            value: item.id,
            label: item.name,
            model: item.model,
            ...item,
          }))
          .map((option) => [option.value, option])
        )
        prev.forEach((option) => {
          if (!map.has(option.value)) {
            map.set(option.value, option)
          }
        })
        return Array.from(map.values())
      })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (!showModal) return
    setSearch('')
    setSelectedVehicles([])
    form.resetFields()
    if (selectedPlant?.id) {
      getSelectedVehicles(selectedPlant.id)
    }
    fetchVehicles('')
  }, [showModal])

  useEffect(() => {
    if (!showModal) return
    fetchVehicles(debouncedSearch)
  }, [debouncedSearch])

  const closeModal = () => {
    setShowModal(false)
    form.resetFields()
    setSelectedVehicles([])
    setSearch('')
  }

  const savePlantVehicles = async () => {
    if (!selectedPlant?.id) return
    const payload = {
      vehicles: selectedVehicles.map((vehicle) => ({
        id: vehicle.id ?? vehicle.value,
        name: vehicle.name,
        model: vehicle.model,
      })),
    }
    let res = await postRequest(
      `/api/plants/save_vehicles/${selectedPlant.id}/`,
      payload
    )
    if (res?.status) {
      navDispatch({
        type: 'notification',
        data: {
          notification_type: 'success',
          msg: intl.formatMessage({ id: 'plants.vehiclesSuccess' }),
        },
      })
      closeModal()
    } else {
      navDispatch({
        type: 'notification',
        data: {
          notification_type: 'error',
          msg: intl.formatMessage({ id: 'plants.vehiclesError' }),
        },
      })
    }
  }

  return (
    <Modal
      open={showModal}
      title={intl.formatMessage({ id: 'plants.addEditVehicles' })}
      width="50%"
      className="plants-form-modal"
      centered={true}
      footer={null}
      closable={true}
      onCancel={closeModal}
    >
      <div className="container">
        <Form form={form} layout="vertical">
          <SelectInput
            name="vehicles"
            label={intl.formatMessage({ id: 'plants.selectVehicle' })}
            options={vehiclesOptions}
            isMulti={true}
            isRequired={false}
            showLoader={loading}
            showSearch={true}
            placeholder={intl.formatMessage({ id: 'plants.selectVehicle' })}
            onInputChange={(value) => setSearch(value || '')}
            notFoundContent={
              vehicleSearchFailed ? (
                <Typography.Text type="danger" style={{ color: 'var(--color-form-link-danger)' }}>
                  {intl.formatMessage({ id: 'generic.searchFailed' })}
                </Typography.Text>
              ) : undefined
            }
            onChange={(options) => setSelectedVehicles(options)}
            maxTagCount={5}
          />
        </Form>
        <Flex justify="end" className="py-4">
          <Button type="primary" onClick={savePlantVehicles}>
            {intl.formatMessage({ id: 'plants.saveVehicles' })}
          </Button>
        </Flex>
      </div>
    </Modal>
  )
}

export default PlantVehiclesForm
